import { Premiere } from "@filmweb/sanity-types";
import { format, isPast, isSameYear, isToday, parse } from "date-fns";
import locale from "date-fns/locale/nb";
import Button from "src/components/button/Button";
import WatchlistButton from "src/components/filmwebid/WatchlistButton";
import withWatchlistState from "src/components/filmwebid/withWatchlistState";
import CalendarSvg from "src/components/svg/CalendarSvg";
import { useProgramDatesContext } from "src/lib/contexts/ProgramDatesWrapper";
import { useSelectedCinemaContext } from "src/lib/contexts/SelectedCinemaContext";
import styles from './NoShows.module.css';
import classNames from 'classnames';

//#region [Props]
type NoShowsProps = {
	edi: string;
	movieTitle: string;
	premiere?: Premiere;

};
//#endregion



//#region [Component]
export default function NoShows({ edi, movieTitle, premiere }: NoShowsProps) {
	const datesContext = useProgramDatesContext();
	const cinemaContext = useSelectedCinemaContext();

	let desktopTextToShow: string | React.ReactNode = "Denne filmen har ingen flere visninger. Legg denne filmen i huskelisten og få påminnelse når den kommer på en strømmetjeneste.";
	let mobileTextToShow: string | React.ReactNode | undefined;
	let premiereParsed: Date | undefined;


	if (premiere?.premiereDate) {
		premiereParsed = parse(premiere!.premiereDate, "yyyy-MM-dd", new Date());
	} else if (premiere?.expectedPremiereDate) {
		premiereParsed = parse(premiere!.expectedPremiereDate, "yyyy-MM-dd", new Date());
	}


	if (premiere?.expectedPremiereDate) {
		const isThisYear = isSameYear(premiereParsed!, new Date());
		desktopTextToShow = `"${movieTitle}" har antatt kinopremiere ${format(premiereParsed!, isThisYear ? "MMMM" : "MMMM yyyy", { locale })}. Vil du varsles når billettsalget starter, så kan du legge filmen i huskelisten.`;
		mobileTextToShow = (<>
			<strong>Antatt kinopremiere {format(premiereParsed!, isThisYear ? "MMMM" : "MMMM yyyy", { locale })}.</strong>
			Klikk her for varsling om billettsalg.
		</>);
	} else if (datesContext.availableDates?.[0] && !isToday(datesContext.availableDates![0])) {
		desktopTextToShow = (<>Neste visning for denne filmen er <SInlineDateSelector
			text={format(new Date(datesContext.availableDates![0]), "iiii d. MMMM", { locale })}
			onClick={() => { datesContext.setSelectedDate(datesContext.availableDates![0]); cinemaContext.setSelectedCinemas(null); }}
			icon={<SCalendarSvg />}
			inverse />
		</>);
	/*} else if (premiereParsed && isAfter(premiereParsed, addMonths(new Date(), -2))) {
		desktopTextToShow = `Det er ingen forestillinger av "${movieTitle}" i salg på din kino akkurat nå. Vil du varsles hvis filmen settes opp, så kan du legge filmen i huskelisten.`;
		mobileTextToShow = (<>
			<strong>Ingen forestillinger i salg på din kino akkurat nå.</strong>
			Klikk her for varsling om billettsalg.
		</>);*/
    } else if(premiereParsed && isPast(premiereParsed) && (datesContext.availableDates?.length ?? 0) === 0) {
        desktopTextToShow = `"${movieTitle}" hadde kinopremiere ${format(premiereParsed!, "d. MMMM yyyy", { locale })}. Det er ikke satt opp noen forestillinger av filmen akkurat nå.`;
        mobileTextToShow = (<>
			<strong>Filmen hadde kinopremiere {format(premiereParsed!, "d. MMMM yyyy", { locale })}.</strong>
			Det er ikke satt opp noen forestillinger av filmen akkurat nå.
		</>);
	} else if (premiere?.premiereDate) {
		const isThisYear = isSameYear(premiereParsed!, new Date());
		desktopTextToShow = `"${movieTitle}" har kinopremiere ${format(premiereParsed!, isThisYear ? "d. MMMM" : "d. MMMM yyyy", { locale })}. Vil du varsles når billettsalget starter, så kan du legge filmen i huskelisten.`;
		mobileTextToShow = (<>
			<strong>Kinopremiere {format(premiereParsed!, isThisYear ? "d. MMMM" : "d. MMMM yyyy", { locale })}.</strong>
			Klikk her for varsling om billettsalg.
		</>);
	}


	return <div className={styles.SNoShows}>
		<WLWatchListButton edi={edi} className={styles.SWatchlistIcon} />
		<div className={styles.SMobileText}>
			{mobileTextToShow ? mobileTextToShow : desktopTextToShow}
		</div>
		<div className={styles.SDesktopText}>
			{desktopTextToShow}
		</div>
	</div>;
}
//#endregion

//#region [Styles]
const WLWatchListButton = withWatchlistState(WatchlistButton);

// Inline replacements: use className hooks on components
const SInlineDateSelector = (props: React.ComponentProps<typeof Button>) => (
    <Button {...props} className={classNames(props.className, styles.SInlineDateSelector)} />
);

const SCalendarSvg = (props: React.ComponentProps<typeof CalendarSvg>) => (
    <CalendarSvg {...props} className={classNames(props.className, styles.SCalendarSvg)} />
);
//#endregion
